import React, { useEffect, useRef, useState } from 'react';
import { createCmsVehicle, updateCmsVehicle, uploadVehicleImage } from '../../api/client';
import SearchableSelect from '../common/SearchableSelect';

const initialFormData = {
  name: '',
  brand_id: '',
  price: '',
  price_source: '',
  variant: '',
  model_year: '',
  category: 'Sedan',
  origin: 'assembled',
  powertrain: 'petrol',
  condition: 'new',
  engine: '',
  power: '',
  fuel: 'Petrol',
  transmission: 'Automatic',
  dimensions: '',
  fuel_efficiency: '',
  annual_maint: 40000,
  insurance_pct: 0.025,
  logo: '🚗',
  image_url: '',
  spec_source: '',
  source: 'Official Specs',
  source_date: '',
  battery_kwh: '',
  range_km: '',
  ac_charge_kw: '',
  dc_charge_kw: '',
  safety: '',
  features: '',
  warranty: '',
  service: '',
  ownership: '',
  available_colors: '',
  pricing_notes: '',
};

const VehicleEditor = ({ vehicle, brands = [], onSaveSuccess, onCancel, onDirtyChange }) => {
  const isEdit = Boolean(vehicle && vehicle.id);
  const [formData, setFormData] = useState(initialFormData);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  // Notify parent of dirty state
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Window beforeunload protection
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Initialize form data from vehicle prop
  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle.name || '',
        brand_id: vehicle.brand_id || '',
        price: vehicle.price || '',
        price_source: vehicle.price_source || '',
        variant: vehicle.variant || '',
        model_year: vehicle.model_year || '',
        category: vehicle.category || 'Sedan',
        origin: vehicle.origin || 'assembled',
        powertrain: vehicle.powertrain || 'petrol',
        condition: vehicle.condition || 'new',
        engine: vehicle.engine || '',
        power: vehicle.power || '',
        fuel: vehicle.fuel || 'Petrol',
        transmission: vehicle.transmission || 'Automatic',
        dimensions: vehicle.dimensions || '',
        fuel_efficiency:
          vehicle.fuel_efficiency !== undefined && vehicle.fuel_efficiency !== null
            ? vehicle.fuel_efficiency
            : '',
        annual_maint:
          vehicle.annual_maint !== undefined && vehicle.annual_maint !== null
            ? vehicle.annual_maint
            : 40000,
        insurance_pct:
          vehicle.insurance_pct !== undefined && vehicle.insurance_pct !== null
            ? vehicle.insurance_pct
            : 0.025,
        logo: vehicle.logo || '🚗',
        image_url: vehicle.image_url || '',
        spec_source: vehicle.spec_source || '',
        source: vehicle.source || 'Official Specs',
        source_date: vehicle.source_date || '',
        battery_kwh: vehicle.battery_kwh || '',
        range_km: vehicle.range_km || '',
        ac_charge_kw: vehicle.ac_charge_kw || '',
        dc_charge_kw: vehicle.dc_charge_kw || '',
        safety: vehicle.safety || '',
        features: vehicle.features || '',
        warranty: vehicle.warranty || '',
        service: vehicle.service || '',
        ownership: vehicle.ownership || '',
        available_colors: vehicle.available_colors || '',
        pricing_notes: vehicle.pricing_notes || '',
      });
      setIsDirty(false);
    } else {
      setFormData(initialFormData);
      setIsDirty(false);
    }
  }, [vehicle]);

  // Auto-focus first input on open
  useEffect(() => {
    const timer = setTimeout(() => {
      firstInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
    setError('');
    setSuccessMsg('');
  };

  const handleSafeCancel = () => {
    if (isDirty) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes in this vehicle record. Are you sure you want to discard your edits?',
      );
      if (!confirmDiscard) return;
    }
    setIsDirty(false);
    onCancel();
  };

  // Keyboard accessibility: Escape to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleSafeCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDirty]);

  // Handle vehicle image file upload
  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, or WebP).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size exceeds the 5 MB limit.');
      return;
    }

    setUploadingImage(true);
    setError('');
    try {
      const result = await uploadVehicleImage(file);
      if (result && result.url) {
        handleChange('image_url', result.url);
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    handleChange('image_url', '');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.name.trim()) {
      setError('Vehicle name is required.');
      return;
    }
    if (!formData.brand_id) {
      setError('Please select an associated brand.');
      return;
    }
    if (!formData.price.trim()) {
      setError('Vehicle price is required (e.g. PKR 4,500,000).');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      const payload = {
        name: formData.name.trim(),
        brand_id: parseInt(formData.brand_id, 10),
        price: formData.price.trim(),
        price_source: formData.price_source.trim() || undefined,
        variant: formData.variant.trim() || undefined,
        model_year: formData.model_year ? String(formData.model_year).trim() : undefined,
        category: formData.category || undefined,
        origin: formData.origin || undefined,
        powertrain: formData.powertrain || undefined,
        condition: formData.condition || undefined,
        engine: formData.engine.trim() || undefined,
        power: formData.power.trim() || undefined,
        fuel: formData.fuel.trim() || undefined,
        transmission: formData.transmission.trim() || undefined,
        dimensions: formData.dimensions.trim() || undefined,
        fuel_efficiency:
          formData.fuel_efficiency !== '' ? parseFloat(formData.fuel_efficiency) : undefined,
        annual_maint: formData.annual_maint !== '' ? parseFloat(formData.annual_maint) : 40000,
        insurance_pct: formData.insurance_pct !== '' ? parseFloat(formData.insurance_pct) : 0.025,
        logo: formData.logo || '🚗',
        image_url: formData.image_url ? formData.image_url.trim() : null,
        spec_source: formData.spec_source.trim() || undefined,
        source: formData.source.trim() || undefined,
        source_date: formData.source_date.trim() || undefined,
        battery_kwh: formData.battery_kwh ? String(formData.battery_kwh).trim() : undefined,
        range_km: formData.range_km ? String(formData.range_km).trim() : undefined,
        ac_charge_kw: formData.ac_charge_kw ? String(formData.ac_charge_kw).trim() : undefined,
        dc_charge_kw: formData.dc_charge_kw ? String(formData.dc_charge_kw).trim() : undefined,
        safety: formData.safety.trim() || undefined,
        features: formData.features.trim() || undefined,
        warranty: formData.warranty.trim() || undefined,
        service: formData.service.trim() || undefined,
        ownership: formData.ownership.trim() || undefined,
        available_colors: formData.available_colors,
        pricing_notes: formData.pricing_notes,
      };

      let result;
      if (isEdit) {
        result = await updateCmsVehicle(vehicle.id, payload);
      } else {
        result = await createCmsVehicle(payload);
      }

      setSuccessMsg('Vehicle saved successfully.');
      setIsDirty(false);
      if (onSaveSuccess) {
        setTimeout(() => {
          onSaveSuccess(result);
        }, 600);
      }
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save vehicle. Verify required fields.');
    } finally {
      setSaving(false);
    }
  };

  const isEV = formData.powertrain === 'ev' || formData.powertrain === 'hybrid';
  const modalTitle = isEdit
    ? `Edit vehicle — ${formData.name || vehicle?.name || 'AXC'}`
    : 'Create new vehicle';

  return (
    <div
      className="cms-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleSafeCancel();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cms-modal-heading"
    >
      <div className="cms-modal-dialog" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        {/* Sticky Modal Header */}
        <div className="cms-modal-header">
          <h2 id="cms-modal-heading">{modalTitle}</h2>
          <button
            type="button"
            className="cms-modal-close"
            onClick={handleSafeCancel}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Scrollable Modal Body */}
        <div className="cms-modal-body">
          {error && (
            <div className="dash-alert">
              <i className="fas fa-circle-exclamation" /> {error}
            </div>
          )}

          {successMsg && (
            <div className="dash-alert success">
              <i className="fas fa-circle-check" /> {successMsg}
            </div>
          )}

          <form id="cms-vehicle-form" onSubmit={handleSubmit} className="cms-editor-form">
            {/* Section 1: Basic Information */}
            <div className="cms-section-card">
              <div className="cms-section-head">
                <h3>
                  <i className="fas fa-circle-info" /> 1. Basic Information
                </h3>
              </div>

              <div className="cms-form-grid">
                <div className="cms-form-group">
                  <label htmlFor="field-brand">
                    Automobile Brand <span className="cms-req">*</span>
                  </label>
                  <SearchableSelect
                    id="field-brand"
                    name="brand_id"
                    value={formData.brand_id}
                    onChange={(e) => handleChange('brand_id', e.target.value)}
                    required
                    placeholder="-- Select Brand --"
                    searchPlaceholder="Search automobile brand (e.g. Chery, Toyota)..."
                    options={[
                      { value: '', label: '-- Select Brand --' },
                      ...brands.map((b) => ({
                        value: String(b.id),
                        label: `${b.name} (${b.slug})`,
                        icon: b.logo || '🚗',
                      })),
                    ]}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-name">
                    Vehicle Model Name <span className="cms-req">*</span>
                  </label>
                  <input
                    id="field-name"
                    type="text"
                    placeholder="e.g. Yaris, Civic, Falcon"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-variant">Trim / Variant</label>
                  <input
                    id="field-variant"
                    type="text"
                    placeholder="e.g. 1.5 CVT, RS, GLX"
                    value={formData.variant}
                    onChange={(e) => handleChange('variant', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-year">Model Year</label>
                  <input
                    id="field-year"
                    type="text"
                    placeholder="e.g. 2025, 2026, 2027"
                    value={formData.model_year}
                    onChange={(e) => handleChange('model_year', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-available-colors">Available Colors</label>
                  <input
                    id="field-available-colors"
                    type="text"
                    placeholder="e.g., Azure blue, Cosmic red, Pearl white"
                    value={formData.available_colors}
                    onChange={(e) => handleChange('available_colors', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-category">Body Category</label>
                  <SearchableSelect
                    id="field-category"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    options={[
                      { value: 'Sedan', label: 'Sedan', icon: '🚗' },
                      { value: 'Hatchback', label: 'Hatchback', icon: '🚗' },
                      { value: 'SUV', label: 'SUV', icon: '🚙' },
                      { value: 'Crossover', label: 'Crossover', icon: '🚙' },
                      { value: 'Pickup', label: 'Pickup', icon: '🛻' },
                      { value: 'Van', label: 'Van', icon: '🚐' },
                      { value: 'MPV', label: 'MPV', icon: '🚐' },
                    ]}
                    placeholder="Select body category..."
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-logo">Vehicle Logo Emoji</label>
                  <SearchableSelect
                    id="field-logo"
                    value={formData.logo}
                    onChange={(e) => handleChange('logo', e.target.value)}
                    options={[
                      { value: '🚗', label: 'Standard Car', icon: '🚗' },
                      { value: '⚡', label: 'Electric Vehicle', icon: '⚡' },
                      { value: '🚙', label: 'SUV / Crossover', icon: '🚙' },
                      { value: '🏎️', label: 'Sports / Performance', icon: '🏎️' },
                      { value: '🚚', label: 'Commercial / Truck', icon: '🚚' },
                    ]}
                    placeholder="Select emoji..."
                  />
                </div>
              </div>

              {/* Vehicle Image Upload Block */}
              <div style={{ padding: '0 24px 24px' }}>
                <div className="cms-form-group">
                  <label>Vehicle Image</label>
                  <div className="cms-image-upload-wrap">
                    <div className="cms-img-preview-box">
                      {formData.image_url ? (
                        <img
                          src={formData.image_url}
                          alt="Vehicle preview"
                          className="cms-img-preview"
                        />
                      ) : (
                        <div className="cms-img-placeholder">
                          <span>{formData.logo || '🚗'}</span>
                        </div>
                      )}
                    </div>
                    <div className="cms-image-upload-content">
                      <div className="cms-image-upload-buttons">
                        <button
                          type="button"
                          className="btn-upload"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? (
                            <>
                              <i className="fas fa-spinner fa-spin" /> Uploading…
                            </>
                          ) : (
                            <>
                              <i className="fas fa-arrow-up-from-bracket" />{' '}
                              {formData.image_url ? 'Replace image' : 'Upload image'}
                            </>
                          )}
                        </button>
                        {formData.image_url && !uploadingImage && (
                          <button
                            type="button"
                            className="btn-remove-img"
                            onClick={handleRemoveImage}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <p className="cms-img-help">PNG, JPG, WEBP. Max 5 MB.</p>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleImageFileChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Pricing */}
            <div className="cms-section-card">
              <div className="cms-section-head">
                <h3>
                  <i className="fas fa-tag" /> 2. Pricing
                </h3>
              </div>

              <div className="cms-form-grid">
                <div className="cms-form-group">
                  <label htmlFor="field-price">
                    Vehicle Price <span className="cms-req">*</span>
                  </label>
                  <input
                    id="field-price"
                    type="text"
                    placeholder="e.g. PKR 4,800,000"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    required
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-price-source">Price Source Attribution</label>
                  <input
                    id="field-price-source"
                    type="text"
                    placeholder="e.g. Manufacturer Ex-Factory, PakWheels"
                    value={formData.price_source}
                    onChange={(e) => handleChange('price_source', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-condition">Market Condition</label>
                  <SearchableSelect
                    id="field-condition"
                    value={formData.condition}
                    onChange={(e) => handleChange('condition', e.target.value)}
                    options={[
                      { value: 'new', label: 'Brand New', icon: '✨' },
                      { value: 'used', label: 'Pre-Owned / Used', icon: '🏷️' },
                    ]}
                    placeholder="Select condition..."
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-origin">Assembly & Origin</label>
                  <SearchableSelect
                    id="field-origin"
                    value={formData.origin}
                    onChange={(e) => handleChange('origin', e.target.value)}
                    options={[
                      { value: 'assembled', label: 'Assembled in Pakistan', icon: '🇵🇰' },
                      { value: 'imported', label: 'Imported CBU', icon: '🌐' },
                      { value: 'chinese', label: 'Chinese Manufacturer', icon: '🇨🇳' },
                    ]}
                    placeholder="Select origin..."
                  />
                </div>
              </div>

              <div className="cms-form-row full-width" style={{ paddingTop: 0 }}>
                <div className="cms-form-group">
                  <label htmlFor="field-pricing-notes">Pricing Notes & Conditions</label>
                  <textarea
                    id="field-pricing-notes"
                    rows={3}
                    placeholder="e.g., Pearl White: Additional PKR 50,000 charges. Freight: PKR 85,000."
                    value={formData.pricing_notes}
                    onChange={(e) => handleChange('pricing_notes', e.target.value)}
                  />
                  <p className="cms-img-help">Enter color surcharges, freight notes, or booking conditions.</p>
                </div>
              </div>
            </div>

            {/* Section 3: Mechanical Specifications */}
            <div className="cms-section-card">
              <div className="cms-section-head">
                <h3>
                  <i className="fas fa-cogs" /> 3. Mechanical Specifications
                </h3>
              </div>

              <div className="cms-form-grid">
                <div className="cms-form-group">
                  <label htmlFor="field-powertrain">Powertrain Type</label>
                  <SearchableSelect
                    id="field-powertrain"
                    value={formData.powertrain}
                    onChange={(e) => handleChange('powertrain', e.target.value)}
                    options={[
                      { value: 'petrol', label: 'Petrol (ICE)', icon: '⛽' },
                      { value: 'diesel', label: 'Diesel (ICE)', icon: '🛢️' },
                      { value: 'hybrid', label: 'Hybrid (HEV / PHEV)', icon: '🔋' },
                      { value: 'ev', label: 'Pure Electric (BEV)', icon: '⚡' },
                    ]}
                    placeholder="Select powertrain..."
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-engine">Engine Displacement</label>
                  <input
                    id="field-engine"
                    type="text"
                    placeholder="e.g. 1498cc, 1.5L Turbo, Electric Motor"
                    value={formData.engine}
                    onChange={(e) => handleChange('engine', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-power">Horsepower & Output</label>
                  <input
                    id="field-power"
                    type="text"
                    placeholder="e.g. 118 hp @ 6000 RPM, 204 hp"
                    value={formData.power}
                    onChange={(e) => handleChange('power', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-fuel">Fuel Type</label>
                  <input
                    id="field-fuel"
                    type="text"
                    placeholder="e.g. Petrol, Diesel, Electric"
                    value={formData.fuel}
                    onChange={(e) => handleChange('fuel', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-trans">Transmission</label>
                  <input
                    id="field-trans"
                    type="text"
                    placeholder="e.g. 7-Speed CVT, 6-Speed Automatic, 1-Speed Direct"
                    value={formData.transmission}
                    onChange={(e) => handleChange('transmission', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-efficiency">Fuel Efficiency (km/L or km/kWh)</label>
                  <input
                    id="field-efficiency"
                    type="number"
                    step="0.1"
                    placeholder="e.g. 14.5"
                    value={formData.fuel_efficiency}
                    onChange={(e) => handleChange('fuel_efficiency', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-dimensions">Dimensions (L x W x H)</label>
                  <input
                    id="field-dimensions"
                    type="text"
                    placeholder="e.g. 4425 x 1730 x 1475 mm"
                    value={formData.dimensions}
                    onChange={(e) => handleChange('dimensions', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-spec-source">Specification Source</label>
                  <input
                    id="field-spec-source"
                    type="text"
                    placeholder="e.g. Official Brochure, PakWheels Test"
                    value={formData.spec_source}
                    onChange={(e) => handleChange('spec_source', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: EV Specifications */}
            <div className={`cms-section-card ${isEV ? 'ev-highlight' : ''}`}>
              <div className="cms-section-head">
                <h3>
                  <i className="fas fa-bolt" /> 4. EV Specifications {isEV ? '(Active)' : '(Optional)'}
                </h3>
                <span className="cms-section-note">
                  Populate for BEV and Hybrid models to enable EV comparison cards.
                </span>
              </div>

              <div className="cms-form-grid">
                <div className="cms-form-group">
                  <label htmlFor="field-battery">Battery Capacity (kWh)</label>
                  <input
                    id="field-battery"
                    type="text"
                    placeholder="e.g. 60.5, 82.5"
                    value={formData.battery_kwh}
                    onChange={(e) => handleChange('battery_kwh', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-range">Electric Driving Range (km)</label>
                  <input
                    id="field-range"
                    type="text"
                    placeholder="e.g. 450, 520"
                    value={formData.range_km}
                    onChange={(e) => handleChange('range_km', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-ac">AC Charging Speed (kW)</label>
                  <input
                    id="field-ac"
                    type="text"
                    placeholder="e.g. 7.4, 11"
                    value={formData.ac_charge_kw}
                    onChange={(e) => handleChange('ac_charge_kw', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-dc">DC Fast Charging Speed (kW)</label>
                  <input
                    id="field-dc"
                    type="text"
                    placeholder="e.g. 80, 150"
                    value={formData.dc_charge_kw}
                    onChange={(e) => handleChange('dc_charge_kw', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Features & Safety */}
            <div className="cms-section-card">
              <div className="cms-section-head">
                <h3>
                  <i className="fas fa-shield-halved" /> 5. Features & Safety
                </h3>
              </div>

              <div className="cms-form-row full-width">
                <div className="cms-form-group">
                  <label htmlFor="field-features">Equipment & Comfort Features</label>
                  <textarea
                    id="field-features"
                    rows={3}
                    placeholder="e.g. Panoramic Sunroof, Adaptive Cruise, Leather Upholstery, 10.25-inch Touchscreen, Apple CarPlay"
                    value={formData.features}
                    onChange={(e) => handleChange('features', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-safety">Safety Specifications & ADAS</label>
                  <textarea
                    id="field-safety"
                    rows={3}
                    placeholder="e.g. 6 Airbags, ABS with EBD, ESC, Hill-Start Assist, Lane Keep Assist, Autonomous Emergency Braking"
                    value={formData.safety}
                    onChange={(e) => handleChange('safety', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Warranty & Ownership */}
            <div className="cms-section-card">
              <div className="cms-section-head">
                <h3>
                  <i className="fas fa-wrench" /> 6. Warranty & Ownership
                </h3>
              </div>

              <div className="cms-form-grid">
                <div className="cms-form-group">
                  <label htmlFor="field-warranty">Manufacturer Warranty</label>
                  <input
                    id="field-warranty"
                    type="text"
                    placeholder="e.g. 3 Years / 100,000 km"
                    value={formData.warranty}
                    onChange={(e) => handleChange('warranty', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-service">Service & Parts Availability</label>
                  <input
                    id="field-service"
                    type="text"
                    placeholder="e.g. 3S Dealership network across 25 cities"
                    value={formData.service}
                    onChange={(e) => handleChange('service', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-ownership">Ownership & Resale Index</label>
                  <input
                    id="field-ownership"
                    type="text"
                    placeholder="e.g. High resale value, readily available parts"
                    value={formData.ownership}
                    onChange={(e) => handleChange('ownership', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-maint">Annual Maintenance Estimate (PKR)</label>
                  <input
                    id="field-maint"
                    type="number"
                    placeholder="e.g. 40000"
                    value={formData.annual_maint}
                    onChange={(e) => handleChange('annual_maint', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-insurance">Insurance Rate Ratio (e.g. 0.025 for 2.5%)</label>
                  <input
                    id="field-insurance"
                    type="number"
                    step="0.001"
                    placeholder="e.g. 0.025"
                    value={formData.insurance_pct}
                    onChange={(e) => handleChange('insurance_pct', e.target.value)}
                  />
                </div>

                <div className="cms-form-group">
                  <label htmlFor="field-source">Data Source Attribution</label>
                  <input
                    id="field-source"
                    type="text"
                    placeholder="e.g. Official OEM Portal, Press Release"
                    value={formData.source}
                    onChange={(e) => handleChange('source', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Sticky Modal Footer */}
        <div className="cms-modal-footer">
          <div className="cms-modal-footer-left">
            {isDirty && (
              <span className="cms-unsaved-badge">
                <i className="fas fa-circle-dot" /> Unsaved changes
              </span>
            )}
          </div>
          <div className="cms-modal-footer-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleSafeCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-save"
              onClick={handleSubmit}
              disabled={saving || (!isDirty && isEdit)}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Saving…
                </>
              ) : (
                'Save Vehicle'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleEditor;
